import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import { addressPartsFromPlace, loadGooglePlaces } from '../../utils/googlePlaces';

const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;

/**
 * Address input with Google Places suggestions. Without a Maps key (or if the script fails)
 * it is a plain text field, so typing an address always works.
 */
const AddressAutocomplete = ({
    value,
    onChange,
    onPlaceSelected,
    label = 'Address',
    countries = ['za'],
    error = false,
    helperText,
    name = 'address',
}) => {
    const [maps, setMaps] = useState(null);
    const [options, setOptions] = useState([]);
    const serviceRef = useRef(null);
    const tokenRef = useRef(null);
    const timerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        loadGooglePlaces().then((loaded) => {
            if (cancelled || !loaded) return;
            serviceRef.current = new loaded.places.AutocompleteService();
            setMaps(loaded);
        });
        return () => {
            cancelled = true;
            clearTimeout(timerRef.current);
        };
    }, []);

    const fetchPredictions = (input) => {
        clearTimeout(timerRef.current);
        if (!serviceRef.current || String(input || '').trim().length < MIN_CHARS) {
            setOptions([]);
            return;
        }
        timerRef.current = setTimeout(() => {
            if (!tokenRef.current) tokenRef.current = new maps.places.AutocompleteSessionToken();
            serviceRef.current.getPlacePredictions({
                input,
                sessionToken: tokenRef.current,
                componentRestrictions: countries.length ? { country: countries } : undefined,
            }, (predictions) => setOptions(predictions || []));
        }, DEBOUNCE_MS);
    };

    const handleSelect = (prediction) => {
        if (!prediction || typeof prediction === 'string') return;
        onChange(prediction.description);
        if (onPlaceSelected && maps) {
            const service = new maps.places.PlacesService(document.createElement('div'));
            service.getDetails({
                placeId: prediction.place_id,
                fields: ['address_components', 'formatted_address'],
                sessionToken: tokenRef.current,
            }, (place) => {
                if (place) onPlaceSelected({ ...addressPartsFromPlace(place), address: place.formatted_address || prediction.description });
            });
        }
        tokenRef.current = null;
    };

    if (!maps) {
        return (
            <TextField
                variant="outlined"
                fullWidth
                size="small"
                label={label}
                name={name}
                value={value || ''}
                onChange={event => onChange(event.target.value)}
                error={error}
                helperText={helperText}
            />
        );
    }

    return (
        <Autocomplete
            freeSolo
            filterOptions={x => x}
            options={options}
            getOptionLabel={option => (typeof option === 'string' ? option : option?.description || '')}
            inputValue={value || ''}
            onInputChange={(event, text, reason) => {
                if (reason === 'reset') return;
                onChange(text);
                fetchPredictions(text);
            }}
            onChange={(event, option) => handleSelect(option)}
            renderOption={option => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <LocationOnOutlinedIcon fontSize="small" color="action" />
                    {option.description}
                </span>
            )}
            renderInput={params => (
                <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    label={label}
                    name={name}
                    error={error}
                    helperText={helperText}
                    inputProps={{ ...params.inputProps, autoComplete: 'new-password' }}
                />
            )}
        />
    );
};

AddressAutocomplete.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onPlaceSelected: PropTypes.func,
    label: PropTypes.string,
    countries: PropTypes.arrayOf(PropTypes.string),
    error: PropTypes.bool,
    helperText: PropTypes.node,
    name: PropTypes.string,
};

export default AddressAutocomplete;
